import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FILE_NAME_TOKENS,
  TOKEN_LABELS,
  parseTemplate,
  serializeTemplate,
  previewFileName,
} from './offerFileName';
import '../styles/offerFileNameBuilder.css';

const SortableToken = ({ token, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: token });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div ref={setNodeRef} className="offer-file-name-token" style={style} {...attributes} {...listeners}>
      <span className="offer-file-name-token__handle">⋮⋮</span>
      <span className="offer-file-name-token__label">{TOKEN_LABELS[token]}</span>
      <button
        type="button"
        className="offer-file-name-token__remove"
        aria-label={`Remove ${TOKEN_LABELS[token]}`}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(token);
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        ×
      </button>
    </div>
  );
};

const OfferFileNameBuilder = ({ value, onChange, venueName, venueCity }) => {
  const tokens = parseTemplate(value);
  const available = FILE_NAME_TOKENS.filter((token) => !tokens.includes(token));
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const commit = (next) => onChange(serializeTemplate(next));

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = tokens.indexOf(active.id);
    const newIndex = tokens.indexOf(over.id);
    const next = [...tokens];
    next.splice(oldIndex, 1);
    next.splice(newIndex, 0, active.id);
    commit(next);
  };

  const addToken = (token) => commit([...tokens, token]);
  const removeToken = (token) => commit(tokens.filter((t) => t !== token));

  return (
    <div className="offer-file-name-builder p-3 rounded">
      <div className="offer-settings-card__title">
        Offer & Settlement File Name
      </div>
      <div className="offer-file-name-builder__description small text-muted">
        Choose which fields appear in generated offer/settlement file names and drag to reorder.
        When no hold or confirm date is attached, the Date field shows <strong>MAD</strong> (Mutually
        Agreeable Date). Leave empty to use the default naming.
      </div>

      <div className="offer-file-name-builder__canvas mt-3">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={tokens} strategy={horizontalListSortingStrategy}>
            <div className="offer-file-name-builder__tokens">
              {tokens.map((token) => (
                <SortableToken key={token} token={token} onRemove={removeToken} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        {tokens.length === 0 && (
          <div className="small text-muted">No fields selected — using the default name.</div>
        )}
      </div>

      {available.length > 0 && (
        <div className="mt-2">
          <span className="offer-file-name-builder__add-label small text-muted">Add field:</span>
          {available.map((token) => (
            <button
              key={token}
              type="button"
              className="offer-file-name-builder__add-btn team-btn-secondary"
              onClick={() => addToken(token)}
            >
              <span>+ {TOKEN_LABELS[token]}</span>
            </button>
          ))}
        </div>
      )}

      <div className="offer-file-name-builder__preview mt-3 small">
        <div className="offer-file-name-builder__preview-line">
          <span className="text-muted">With a date:&nbsp;</span>
          <code>{previewFileName(tokens, { withDate: true, venue: venueName, city: venueCity })}</code>
        </div>
        <div className="offer-file-name-builder__preview-line">
          <span className="text-muted">No date attached:&nbsp;</span>
          <code>{previewFileName(tokens, { withDate: false, venue: venueName, city: venueCity })}</code>
        </div>
      </div>
    </div>
  );
};

export default OfferFileNameBuilder;
